import { parseZonedDateTime } from '@rematter/common/dateTime';
import { Unit } from '@rematter/frontend-lib-api-client';

import { BrokeredOrderDetailRecord } from '../types/brokeredOrder';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const brokeredOrderDetailQueryAtom = createQueryAtomFactory(
  'brokerage.brokeredOrderDetail',
  (params: { id: string }) => ({
    brokeredOrder: {
      __args: {
        id: params.id,
      },
      id: true,
      orderNumber: true,
      status: true,
      suppliers: {
        id: true,
        name: true,
      },
      customers: {
        id: true,
        name: true,
      },
      purchaseTotal: {
        amount: true,
        currencyCode: true,
      },
      salesTotal: {
        amount: true,
        currencyCode: true,
      },
      margin: {
        amount: true,
        currencyCode: true,
      },
      marginPercent: true,
      notes: true,
      lineItems: {
        id: true,
        brokeredOrderId: true,
        itemId: true,
        item: {
          id: true,
          name: true,
        },
        quantity: true,
        unit: true,
        purchaseUnitPrice: {
          amount: true,
          currencyCode: true,
        },
        salesUnitPrice: {
          amount: true,
          currencyCode: true,
        },
        purchaseTotal: {
          amount: true,
          currencyCode: true,
        },
        salesTotal: {
          amount: true,
          currencyCode: true,
        },
        margin: {
          amount: true,
          currencyCode: true,
        },
        marginPercent: true,
        notes: true,
      },
      purchaseOrders: {
        id: true,
        documentNumber: true,
        status: true,
        account: {
          id: true,
          name: true,
          addresses: {
            id: true,
            address: {
              streetAddress: true,
              streetAddress2: true,
              city: true,
              state: true,
              postalCode: true,
            },
          },
        },
        lineItems: {
          id: true,
          totalPrice: {
            amount: true,
            currencyCode: true,
          },
        },
      },
      salesOrders: {
        id: true,
        documentNumber: true,
        status: true,
        account: {
          id: true,
          name: true,
          addresses: {
            id: true,
            address: {
              streetAddress: true,
              streetAddress2: true,
              city: true,
              state: true,
              postalCode: true,
            },
          },
        },
        lineItems: {
          id: true,
        },
        totalPrice: {
          amount: true,
          currencyCode: true,
        },
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: (data): BrokeredOrderDetailRecord | null => {
      if (!data?.brokeredOrder) {
        return null;
      }

      const order = data.brokeredOrder;
      return {
        id: order.id,
        orderNumber: order.orderNumber,
        status: order.status,
        suppliers: (order.suppliers ?? []).map((s) => ({
          id: s.id,
          name: s.name,
        })),
        customers: (order.customers ?? []).map((c) => ({
          id: c.id,
          name: c.name,
        })),
        purchaseTotal: order.purchaseTotal,
        salesTotal: order.salesTotal,
        margin: order.margin,
        marginPercent: order.marginPercent,
        notes: order.notes ?? null,
        lineItems: (order.lineItems ?? []).map((item) => ({
          id: item.id,
          brokeredOrderId: item.brokeredOrderId,
          itemId: item.itemId,
          item: item.item ?? null,
          quantity: item.quantity,
          unit: item.unit as Unit,
          purchaseUnitPrice: item.purchaseUnitPrice,
          salesUnitPrice: item.salesUnitPrice,
          purchaseTotal: item.purchaseTotal,
          salesTotal: item.salesTotal,
          margin: item.margin,
          marginPercent: item.marginPercent,
          notes: item.notes ?? null,
        })),
        purchaseOrders: (order.purchaseOrders ?? []).map((po) => {
          const lineItems = po.lineItems ?? [];
          const total = lineItems.reduce(
            (acc, li) => ({
              amount: acc.amount + (li.totalPrice?.amount ?? 0),
              currencyCode: li.totalPrice?.currencyCode ?? 'USD',
            }),
            { amount: 0, currencyCode: 'USD' },
          );
          return {
            id: po.id,
            documentNumber: po.documentNumber,
            status: po.status,
            accountName: po.account?.name ?? null,
            address: (po.account?.addresses ?? []).map((addr) => ({
              id: addr.id,
              street1: addr.address?.streetAddress,
              street2: addr.address?.streetAddress2 ?? undefined,
              city: addr.address?.city,
              state: addr.address?.state,
              postalCode: addr.address?.postalCode,
            })),
            lineItemCount: lineItems.length,
            total: total.amount > 0 ? total : null,
          };
        }),
        salesOrders: (order.salesOrders ?? []).map((so) => ({
          id: so.id,
          documentNumber: so.documentNumber,
          status: so.status,
          accountName: so.account?.name ?? null,
          address: (so.account?.addresses ?? []).map((addr) => ({
            id: addr.id,
            street1: addr.address?.streetAddress,
            street2: addr.address?.streetAddress2 ?? undefined,
            city: addr.address?.city,
            state: addr.address?.state,
            postalCode: addr.address?.postalCode,
          })),
          lineItemCount: so.lineItems?.length ?? 0,
          total: so.totalPrice ?? null,
        })),
        timestamps: {
          createdAt: parseZonedDateTime(order.timestamps.createdAt),
          updatedAt: parseZonedDateTime(order.timestamps.updatedAt),
        },
      };
    },
  },
);
